import {
  collection,
  onSnapshot,
  orderBy,
  query,
  QueryDocumentSnapshot,
  where,
} from 'firebase/firestore';
import {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router';

import { AnalyticsFilters } from '@superfunnel/types/analytics-filter';
import { Domain } from '@superfunnel/types/domain';
import { DynamicPage } from '@superfunnel/types/dynamic-page';
import { DynamicPageVariant } from '@superfunnel/types/dynamic-page-variant';
import { Website } from '@superfunnel/types/website';

import { db } from '../Firebase';
import { getAnalyticsByDates } from '../services/AnalyticsService';
import { dynamicPageVariantsColRef } from '../services/DynamicPageVariantsService';
import {
  analyticsRangeToDates,
  emptyConverter,
  getDynamicPageRelevantDomains,
  getPrimaryDomain,
} from '../utils';
import { useDashboardContext } from './DashboardContext';
import { customEventDefinitionsKey } from '../utils/customEventDefinitionsKey';
import { UserContext } from './UserContext';

interface DynamicPageContextProps {
  selectedDynamicPage: QueryDocumentSnapshot<DynamicPage> | undefined;
  websites: QueryDocumentSnapshot<Website>[] | undefined;
  variants: QueryDocumentSnapshot<DynamicPageVariant>[] | undefined;
  customRangeAnalytics: any | undefined;
  hasChangesToDeploy: boolean | undefined;
  primaryDomain: QueryDocumentSnapshot<Domain> | undefined;
  relevantDomains: QueryDocumentSnapshot<Domain>[] | undefined;
  /** Analytics filters shared with the account-level dashboard so they carry over from the list view */
  analyticsFilters: AnalyticsFilters;
  setAnalyticsFilters: Dispatch<SetStateAction<AnalyticsFilters>>;
  resetAnalyticsFilters: () => void;
}

export const DynamicPageContext = createContext<DynamicPageContextProps | null>(
  null,
);

export function DynamicPageContextProvider(props: any) {
  const location = useLocation();

  const { selectedAccount, analyticsCustomEvents } = useContext(UserContext);
  const {
    analyticsRange,
    domains,
    dynamicPages,
    analyticsFilters,
    setAnalyticsFilters,
    resetAnalyticsFilters,
  } = useDashboardContext();

  const [websites, setWebsites] = useState<QueryDocumentSnapshot<Website>[]>();
  const [customRangeAnalytics, setCustomRangeAnalytics] = useState<any>();
  const [variants, setVariants] =
    useState<QueryDocumentSnapshot<DynamicPageVariant>[]>();

  const selectedDynamicPage = useMemo(() => {
    const dynamicPageId = location.pathname.split('/')[4];
    if (dynamicPageId === 'pages') {
      const dynamicPageId = location.pathname.split('/')[5];
      return dynamicPages?.find((a) => a.id === dynamicPageId);
    }
    return dynamicPages?.find((a) => a.id === dynamicPageId);
  }, [dynamicPages, location]);

  const hasChangesToDeploy = useMemo(() => {
    return selectedDynamicPage?.data().hasChangesToPublish;
  }, [selectedDynamicPage]);

  const relevantDomains = useMemo(() => {
    if (!domains || !selectedDynamicPage) return domains;

    return getDynamicPageRelevantDomains(domains, selectedDynamicPage?.data());
  }, [domains, selectedDynamicPage]);

  const primaryDomain = useMemo(() => {
    if (!relevantDomains) return;

    return getPrimaryDomain(relevantDomains);
  }, [relevantDomains]);

  useEffect(() => {
    if (!selectedAccount || !selectedDynamicPage) {
      setWebsites(undefined);
      return;
    }

    const accountId = selectedAccount.id;
    const dynamicPageId = selectedDynamicPage.id;

    const websitesColRef = collection(
      db,
      'accounts',
      accountId,
      'campaigns',
      dynamicPageId,
      'websites',
    ).withConverter(emptyConverter<Website>());
    const unsubscribe = onSnapshot(
      query(
        websitesColRef,
        where('status', 'in', ['draft', 'active', 'disabled', 'creating']),
        orderBy('createdAt', 'asc'),
      ),
      (websites) => {
        setWebsites(websites.docs);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [selectedAccount, selectedDynamicPage]);

  useEffect(() => {
    if (!selectedAccount || !selectedDynamicPage) {
      setVariants(undefined);
      return;
    }

    const accountId = selectedAccount.id;
    const dynamicPageId = selectedDynamicPage.id;

    const variantsColRef = dynamicPageVariantsColRef(accountId, dynamicPageId);
    const unsubscribe = onSnapshot(
      query(
        variantsColRef,
        where('status', 'in', ['draft', 'active', 'disabled']),
        orderBy('createdAt', 'asc'),
      ),
      (variants) => {
        setVariants(variants.docs);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [selectedAccount, selectedDynamicPage]);

  // Depend on the account/dynamic page IDs (not the full Firestore snapshots)
  // so that unrelated document changes — e.g. toggling a variant's status, which
  // touches `hasChangesToPublish`/`lastEditedAt` on the dynamic page — do not
  // trigger an unnecessary analytics refetch.
  const analyticsAccountId = selectedAccount?.id;
  const analyticsDynamicPageId = selectedDynamicPage?.id;
  const customEventsKey = customEventDefinitionsKey(analyticsCustomEvents);

  useEffect(() => {
    setCustomRangeAnalytics(undefined);

    if (!analyticsAccountId || !analyticsDynamicPageId) {
      return;
    }

    const { startDate, endDate } = analyticsRangeToDates(analyticsRange);

    let relevant = true;
    getAnalyticsByDates(
      analyticsAccountId,
      'campaign',
      analyticsDynamicPageId,
      startDate,
      endDate,
      analyticsFilters.rules.length ? analyticsFilters.rules : undefined,
    )
      .then((res) => {
        if (!relevant) return;

        setCustomRangeAnalytics(res);
      })
      .catch((err) => {
        console.error('Error fetching custom range analytics:', err);
      });

    return () => {
      relevant = false;
    };
  }, [
    analyticsAccountId,
    analyticsDynamicPageId,
    analyticsRange,
    analyticsFilters,
    customEventsKey,
  ]);

  return (
    <DynamicPageContext.Provider
      value={{
        selectedDynamicPage,
        websites,
        variants,
        customRangeAnalytics,
        hasChangesToDeploy,
        relevantDomains,
        primaryDomain,
        analyticsFilters,
        setAnalyticsFilters,
        resetAnalyticsFilters,
      }}
    >
      {props.children}
    </DynamicPageContext.Provider>
  );
}

export function useDynamicPageContext() {
  const context = useContext(DynamicPageContext);
  if (!context) {
    throw new Error(
      'useDynamicPageContext must be used within a DynamicPageContextProvider',
    );
  }
  return context;
}
